/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { ContentZonesService } from '@sm360/widget-commons-core-services';

import CashPayment from 'commons-vehicle/Vehicle/components/PaymentV2/CashPayment/CashPayment.jsx';
import Financing from 'commons-vehicle/Vehicle/components/PaymentV2/Financing/Financing.jsx';
import Price from 'commons-vehicle/Vehicle/components/PaymentV2/Price/Price.jsx';
import PaymentDetailRebate from 'commons-vehicle/Vehicle/components/PaymentV2/PaymentDetail/PaymentDetailRebate.jsx';
import PURCHASE_METHODS from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';

const PaymentDetailV2 = ({
    purchaseMethods,
    forcedPurchaseMethod,
    isUsed,
    taxCodes,
    includedDealerIncentives,
    displayCreditRate,
}) => {
    const currentMethod = forcedPurchaseMethod || purchaseMethods.getSelectedPurchaseMethodValue();
    const selectedPurchaseMethodAmounts = purchaseMethods.getSelectedPurchaseMethodAmounts();

    const tradeIn = selectedPurchaseMethodAmounts.tradeIn > 0 && (
        <PaymentDetailRebate
            amount={selectedPurchaseMethodAmounts.tradeIn}
            i18nKey="PAYMENTS_REBATES.TRADEIN"
            data={{ 'data-sr': 'TradeIn' }}
            iconClass="icon-refresh"
        />
    );

    const modelElectricRebate = purchaseMethods.getElectricRebate();
    const isThereElectricRebate =
        !isEmpty(modelElectricRebate) && !!modelElectricRebate.rebate && !Number.isNaN(modelElectricRebate.rebate);

    const electricRebate = isThereElectricRebate && (
        <PaymentDetailRebate
            amount={
                currentMethod === PURCHASE_METHODS.CASH
                    ? modelElectricRebate.rebate + modelElectricRebate.taxes
                    : modelElectricRebate.rebate
            }
            i18nKey="PAYMENTS_REBATES.ELECTRIC_REBATE"
            data={{ 'data-sr': 'ElectricRebate' }}
            iconClass="icon-electric-vehicle"
        />
    );

    let paymentMethod = null;
    const { disclaimers } = purchaseMethods;
    const taxesExcludedDisclaimer = disclaimers ? disclaimers.taxesExcluded : '';
    const cashPurchaseAmounts = purchaseMethods.getCashPurchaseAmounts();

    const includedDisclaimerCzLocation = isUsed
        ? 'disclaimer.price.included.used.cash'
        : 'disclaimer.price.included.new';
    const includedDisclaimer = ContentZonesService.getContentZone('widget.commons', includedDisclaimerCzLocation)
        ? ContentZonesService.getContentZone('widget.commons', includedDisclaimerCzLocation).getValue()
        : '';

    const amounts =
        PURCHASE_METHODS.CASH === currentMethod && cashPurchaseAmounts.rebates > selectedPurchaseMethodAmounts.rebates
            ? cashPurchaseAmounts
            : selectedPurchaseMethodAmounts;

    let rebatesAmount;
    if (isThereElectricRebate) {
        if (PURCHASE_METHODS.CASH === currentMethod) {
            rebatesAmount = amounts.rebates - modelElectricRebate.rebate - modelElectricRebate.taxes;
        } else {
            rebatesAmount = amounts.rebates - modelElectricRebate.rebate;
        }
    } else {
        rebatesAmount = amounts.rebates;
    }

    const dealerRebatesAmount =
        PURCHASE_METHODS.CASH === currentMethod
            ? cashPurchaseAmounts.dealerDiscounts
            : selectedPurchaseMethodAmounts.discounts;

    // According to Pat Helie this code is potentially deprecated and
    // job done by back-end but prefer to keep it in the code to remember we have this front-end solution
    if (includedDealerIncentives) {
        rebatesAmount -= cashPurchaseAmounts.dealerIncentives;
    }

    const rebates = rebatesAmount > 0 && (
        <PaymentDetailRebate
            amount={rebatesAmount}
            i18nKey={isUsed ? 'PAYMENTS_REBATES.USED_REBATE' : 'PAYMENTS_REBATES.MANUFACTURER_REBATE'}
            data={{ 'data-sr': 'Rebate' }}
            iconClass="icon-promo2"
        />
    );

    const dealerRebates = dealerRebatesAmount > 0 && (
        <PaymentDetailRebate
            amount={dealerRebatesAmount}
            i18nKey="PAYMENTS_REBATES.DEALER_REBATE"
            data={{ 'data-sr': 'Rebate' }}
            iconClass="icon-promo2"
        />
    );

    if (currentMethod === PURCHASE_METHODS.FINANCE || currentMethod === PURCHASE_METHODS.LEASE) {
        paymentMethod = (
            <Financing
                purchaseMethods={purchaseMethods}
                taxCodes={taxCodes}
                isUsed={isUsed}
                rebates={rebates}
                dealerRebates={dealerRebates}
                electricRebate={electricRebate}
                displayCreditRate={displayCreditRate}
            >
                {tradeIn}
            </Financing>
        );
    } else if (currentMethod === PURCHASE_METHODS.CASH) {
        const msrp =
            selectedPurchaseMethodAmounts.allInPrice > purchaseMethods.cashPurchase.getRetail()
                ? selectedPurchaseMethodAmounts.allInPrice
                : purchaseMethods.cashPurchase.getRetail() + cashPurchaseAmounts.dealerDiscounts;

        let amountPrice =
            selectedPurchaseMethodAmounts.allInPrice > purchaseMethods.cashPurchase.getRetail()
                ? msrp - purchaseMethods.cashPurchase.getTotalRebates()
                : purchaseMethods.cashPurchase.getRetail() - purchaseMethods.cashPurchase.getTotalRebates();

        // According to Pat Helie this code is potentially deprecated and
        // job done by back-end but prefer to keep it in the code to remember we have this front-end solution
        if (includedDealerIncentives) {
            amountPrice += cashPurchaseAmounts.dealerIncentives;
        }

        paymentMethod = (
            <div className="payment-summary-section">
                <CashPayment isUsed={isUsed} msrp={msrp}>
                    {includedDisclaimer && (
                        <span className="payment-row disclaimer is-light sr-text">{includedDisclaimer}</span>
                    )}
                    <div className="payment-rebates">
                        {rebates}
                        {dealerRebates}
                        {electricRebate}
                    </div>
                </CashPayment>
                <Price
                    amount={amountPrice}
                    taxCodes={taxCodes}
                    taxesExcludedDisclaimer={taxesExcludedDisclaimer}
                    isUsed={isUsed}
                />
            </div>
        );
    }
    return paymentMethod;
};

PaymentDetailV2.defaultTypes = {
    forcedPurchaseMethod: null,
    includedDealerIncentives: false,
    config: {},
    displayCreditRate: false,
};

PaymentDetailV2.propTypes = {
    purchaseMethods: PropTypes.shape({
        cashPurchase: PropTypes.object.isRequired,
        financing: PropTypes.object.isRequired,
    }).isRequired,
    forcedPurchaseMethod: PropTypes.string,
    isUsed: PropTypes.bool.isRequired,
    taxCodes: PropTypes.string.isRequired,
    includedDealerIncentives: PropTypes.bool,
    config: PropTypes.object.isRequired,
    displayCreditRate: PropTypes.bool,
};

PaymentDetailV2.displayName = 'PaymentDetailV2';

export default PaymentDetailV2;
